import { useEffect, useId, useRef, useState } from "react";
import { getSessionToken } from "../../../hooks/useSession.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";
const EMAIL_PATTERN = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;

interface Contact {
  email: string;
  name: string | null;
}

/**
 * Address field with chips. Type an address and press Enter, comma, or Tab,
 * or pick one of the suggested staff and agents.
 */
export function RecipientInput({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  autoFocus?: boolean;
}) {
  const id = useId();
  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState<Contact[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const q = draft.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const token = getSessionToken();
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void fetch(`${API_BASE}/api/admin/inbox/contacts?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((response) => response.json())
        .then((json) => {
          if (!cancelled && json?.success) {
            setSuggestions((json.data as Contact[]).filter((contact) => !value.includes(contact.email)));
            setActive(0);
          }
        })
        .catch(() => {});
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [draft, value]);

  function add(raw: string) {
    const address = raw.trim().replace(/[,;]+$/, "").toLowerCase();
    if (!address) return;
    if (!value.includes(address)) onChange([...value, address]);
    setDraft("");
    setSuggestions([]);
  }

  const invalid = (address: string) => !EMAIL_PATTERN.test(address);

  return (
    <div className="relative">
      <label htmlFor={id} className="mb-1 block text-xs font-semibold uppercase tracking-wide text-content-muted">
        {label}
      </label>
      <div
        className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-lg border border-border-default bg-surface-input px-2 py-1.5 focus-within:ring-2 focus-within:ring-accent/40"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((address) => (
          <span
            key={address}
            className={`inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
              invalid(address)
                ? "bg-status-rejected-bg text-status-rejected-text"
                : "bg-surface-muted text-content-primary"
            }`}
          >
            <span className="truncate">{address}</span>
            <button
              type="button"
              className="focus-ring rounded-full px-1 text-content-muted hover:text-content-primary"
              aria-label={`Remove ${address}`}
              onClick={() => onChange(value.filter((entry) => entry !== address))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          autoFocus={autoFocus}
          value={draft}
          role="combobox"
          aria-expanded={suggestions.length > 0}
          aria-controls={`${id}-list`}
          onChange={(event) => {
            const next = event.target.value;
            if (/[,;]\s*$/.test(next)) add(next);
            else setDraft(next);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" && suggestions.length) {
              event.preventDefault();
              setActive((index) => Math.min(index + 1, suggestions.length - 1));
            } else if (event.key === "ArrowUp" && suggestions.length) {
              event.preventDefault();
              setActive((index) => Math.max(index - 1, 0));
            } else if (event.key === "Enter" || (event.key === "Tab" && draft.trim())) {
              if (!draft.trim() && !suggestions.length) return;
              event.preventDefault();
              add(suggestions[active]?.email ?? draft);
            } else if (event.key === "Backspace" && !draft && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => window.setTimeout(() => draft.trim() && add(draft), 120)}
          className="min-w-[10rem] flex-1 bg-transparent py-0.5 text-sm text-content-primary outline-none placeholder:text-content-muted"
          placeholder={value.length ? "" : "Name or email"}
        />
      </div>
      {suggestions.length > 0 && (
        <ul
          id={`${id}-list`}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border-default bg-surface-raised py-1 shadow-card"
        >
          {suggestions.map((contact, index) => (
            <li key={contact.email} role="option" aria-selected={index === active}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => add(contact.email)}
                className={`block w-full px-3 py-2 text-left text-sm ${
                  index === active ? "bg-surface-muted" : "hover:bg-surface-muted"
                }`}
              >
                <span className="font-medium text-content-primary">{contact.name ?? contact.email}</span>
                {contact.name && <span className="block text-xs text-content-muted">{contact.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
